import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import useResponsiveness from '@/hooks/useResponsiveness';
import { BORDER_RADIUS } from '@/constants/config';
import { useModalStore } from '@/store';
import useTranslation from '@/hooks/useTranslation';
import { getMemoryThemeColors } from '@/components/Memory/memoryTheme';

const isWeb = Platform.OS === 'web';

/**
 * MemoryEmptyState
 *
 * Overhauled empty state for the Memory page:
 * - Inspiring hero header with dual-tone glowing brain badge
 * - Value props (Private, Cross-device, Full control)
 * - Two primary on-ramps: Guided Setup & Import from Another AI
 * - One-click starter prompts to add first memory immediately
 */
function MemoryEmptyState({ onSelectExample }) {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM } = useResponsiveness();
  const { openModal } = useModalStore(state => state.actions);

  const memColors = useMemo(() => getMemoryThemeColors(isDark, colors), [isDark, colors]);

  const styles = useMemo(
    () => createStyles(colors, memColors, isDark, isXS, isSM),
    [colors, memColors, isDark, isXS, isSM]
  );

  const FEATURES = useMemo(
    () => [
      {
        icon: 'brain',
        title: t('emptyState.memory.remembersYou'),
        desc: 'Remembers context across all chats',
      },
      {
        icon: 'shield-check',
        title: 'Private & Secure',
        desc: 'Never used to train public models',
      },
      {
        icon: 'sliders',
        title: t('emptyState.memory.fullControl'),
        desc: 'Edit, pause, or remove anytime',
      },
    ],
    [t]
  );

  const EXAMPLE_MEMORIES = useMemo(
    () => [
      { text: t('emptyState.memory.exampleConcise'), icon: 'list-check' },
      { text: t('emptyState.memory.exampleDeveloper'), icon: 'code' },
      { text: t('emptyState.memory.exampleReact'), icon: 'laptop-code' },
      { text: t('emptyState.memory.exampleDarkMode'), icon: 'moon' },
      { text: t('emptyState.memory.exampleSpanish'), icon: 'language' },
      { text: t('emptyState.memory.exampleName'), icon: 'user' },
    ],
    [t]
  );

  const handleExamplePress = useCallback((exampleText) => {
    if (typeof onSelectExample === 'function') {
      onSelectExample(exampleText);
    } else {
      openModal('CreateMemoryModal', { initialContent: exampleText });
    }
  }, [onSelectExample, openModal]);

  const handleOpenMemoryBuilder = useCallback(() => {
    openModal('MemoryBuilderModal');
  }, [openModal]);

  const handleOpenImportMemory = useCallback(() => {
    openModal('ImportMemoryModal');
  }, [openModal]);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {/* Hero Section */}
        <View style={styles.heroSection}>
          <View style={styles.iconContainer}>
            <View style={styles.mainIcon}>
              <Icon name="brain" size={isXS ? 30 : 36} color={memColors.accent} />
            </View>
          </View>

          <Text tag="h2" weight="extraBold" style={styles.title}>
            {t('emptyState.memory.title')}
          </Text>

          <Text size="n" color="secondary" style={styles.subtitle}>
            {t('emptyState.memory.subtitle')}
          </Text>
        </View>

        {/* Feature Highlights Row */}
        <View style={styles.featuresRow}>
          {FEATURES.map((feature, index) => (
            <View key={index} style={styles.featureItem}>
              <View style={styles.featureIconWrapper}>
                <Icon name={feature.icon} size={16} color={colors.text_color_primary} />
              </View>
              <Text size="xs" weight="bold" color="primary" style={styles.featureTitle}>
                {feature.title}
              </Text>
              <Text size="xxs" color="secondary" style={styles.featureDesc}>
                {feature.desc}
              </Text>
            </View>
          ))}
        </View>

        {/* Dual On-Ramps: Guided Setup & Import */}
        <View style={styles.onRampGrid}>
          {/* Card 1: Guided Setup */}
          <View style={styles.onRampCard}>
            <View style={styles.onRampHead}>
              <View style={styles.onRampIcon}>
                <Icon name="wand-magic-sparkles" size={18} color={memColors.accent} />
              </View>
              <View style={styles.onRampTextWrap}>
                <Text size="m" weight="bold" color="primary">
                  {t('emptyState.memory.buildProfileTitle')}
                </Text>
                <Text size="xs" color="secondary" style={styles.onRampDesc}>
                  {t('emptyState.memory.buildProfileDesc')}
                </Text>
              </View>
            </View>

            <Button
              text={t('emptyState.memory.getStarted')}
              variant="primary"
              size="small"
              prependIcon="wand-magic-sparkles"
              onPress={handleOpenMemoryBuilder}
              isFullWidth
            />
          </View>

          {/* Card 2: Import from Another AI */}
          <View style={styles.onRampCard}>
            <View style={styles.onRampHead}>
              <View style={styles.onRampIcon}>
                <Icon name="file-import" size={18} color={colors.text_color_primary} />
              </View>
              <View style={styles.onRampTextWrap}>
                <Text size="m" weight="bold" color="primary">
                  Migrate from Another AI
                </Text>
                <Text size="xs" color="secondary" style={styles.onRampDesc}>
                  Import memories from ChatGPT, Claude, or other assistants in seconds.
                </Text>
              </View>
            </View>

            <Button
              text={t('emptyState.memory.importFromAI')}
              variant="secondary"
              size="small"
              prependIcon="file-import"
              onPress={handleOpenImportMemory}
              isFullWidth
            />
          </View>
        </View>

        {/* Or Add Manually Divider */}
        <View style={styles.orDivider}>
          <View style={styles.dividerLine} />
          <Text size="xs" color="secondary" weight="medium">
            {t('emptyState.memory.orAddManually')}
          </Text>
          <View style={styles.dividerLine} />
        </View>

        {/* Example Starter Memories */}
        <View style={styles.examplesGrid}>
          {EXAMPLE_MEMORIES.map((example, index) => (
            <Pressable
              key={index}
              style={({ hovered }) => [
                styles.examplePill,
                hovered && styles.examplePillHovered,
              ]}
              onPress={() => handleExamplePress(example.text)}
              accessibilityRole="button"
              accessibilityLabel={`Add: ${example.text}`}
            >
              <Icon name="plus" size={10} color={colors.text_color_tertiary} />
              <Text size="s" color="primary" style={styles.exampleText}>
                {example.text}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
    </View>
  );
}

const createStyles = (colors, memColors, isDark, isXS, isSM) =>
  StyleSheet.create({
    container: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: isXS ? 18 : 28,
      paddingHorizontal: isXS ? 12 : 16,
    },
    content: {
      width: '100%',
      maxWidth: 720,
      alignItems: 'center',
    },
    heroSection: {
      alignItems: 'center',
      marginBottom: isXS ? 20 : 24,
    },
    iconContainer: {
      position: 'relative',
      marginBottom: 16,
    },
    mainIcon: {
      width: isXS ? 64 : 76,
      height: isXS ? 64 : 76,
      borderRadius: isXS ? 32 : 38,
      backgroundColor: memColors.accentBg,
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      textAlign: 'center',
      marginBottom: 8,
      fontSize: isXS ? 22 : 26,
      lineHeight: isXS ? 28 : 34,
      letterSpacing: -0.4,
      color: colors.text_color_primary,
    },
    subtitle: {
      textAlign: 'center',
      maxWidth: 440,
      lineHeight: 22,
    },
    featuresRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: isXS ? 10 : 16,
      marginBottom: isXS ? 20 : 26,
      width: '100%',
    },
    featureItem: {
      alignItems: 'center',
      flex: 1,
      maxWidth: 160,
      padding: 10,
      borderRadius: BORDER_RADIUS.medium,
      backgroundColor: colors.solid_color_block,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
    },
    featureIconWrapper: {
      width: 34,
      height: 34,
      borderRadius: 17,
      backgroundColor: colors.solid_color_tertiary,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 6,
    },
    featureTitle: {
      textAlign: 'center',
      marginBottom: 2,
    },
    featureDesc: {
      textAlign: 'center',
      lineHeight: 14,
    },
    onRampGrid: {
      flexDirection: isSM || isXS ? 'column' : 'row',
      gap: 12,
      width: '100%',
      marginBottom: 20,
    },
    onRampCard: {
      flex: 1,
      backgroundColor: colors.solid_color_block,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      borderRadius: BORDER_RADIUS.large,
      padding: isXS ? 14 : 18,
      justifyContent: 'space-between',
      gap: 14,
    },
    onRampHead: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
    },
    onRampIcon: {
      width: 40,
      height: 40,
      borderRadius: 12,
      backgroundColor: colors.solid_color_tertiary,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    onRampTextWrap: {
      flex: 1,
      minWidth: 0,
      gap: 4,
    },
    onRampDesc: {
      lineHeight: 18,
    },
    orDivider: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      gap: 12,
      marginVertical: 14,
    },
    dividerLine: {
      flex: 1,
      height: 1,
      backgroundColor: colors.border_color_secondary,
    },
    examplesGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      justifyContent: 'center',
    },
    examplePill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: 16,
      backgroundColor: colors.solid_color_secondary,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      ...(isWeb ? { cursor: 'pointer', transition: 'all 0.16s ease' } : {}),
    },
    examplePillHovered: {
      backgroundColor: colors.solid_color_tertiary,
      borderColor: colors.border_color_input,
    },
    exampleText: {
      fontSize: 13,
    },
  });

export default React.memo(MemoryEmptyState);
